import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ModernSidebar from './ModernSidebar';
import TopBar from './TopBar';
import LoadingSpinner from '../LoadingSpinner';
import StorageService from '../../services/storage/storageService';
import BASE_URL from '../../utils/storage';
import { resolveCompanyNameFromValue } from '../../hooks/useCompanyName';
import GlobalBackButton from './GlobalBackButton';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext.jsx';
import { FEATURE_CODES, hasFeature } from '../../utils/features.js';
import { languageStore } from '../../i18n/languageStore.js';
import { getProfileRole } from '../../utils/profileRoles.js';

const toLayoutUserData = (profile) => {
  if (!profile) return null;

  const userRole = getProfileRole(profile);

  return {
    name: profile.name || profile.username || 'Utilizator',
    fullName: profile.name || profile.username || 'Utilizator',
    company: resolveCompanyNameFromValue(profile.company, 'AtruX Transport'),
    role: userRole,
    email: profile.email || '',
    phone: profile.phone || '',
  };
};

const DashboardLayout = ({ children, onLogout }) => {
  const [userData, setUserData] = useState(() => (
    toLayoutUserData(StorageService.get('userData'))
  ));
  const [loading, setLoading] = useState(() => !StorageService.get('userData'));
  const navigate = useNavigate();
  const { features } = useFeatureFlags();

  const featureAccess = {
    features,
    hasOptifleetAccess: hasFeature(features, FEATURE_CODES.OPTIFLEET),
    hasProfitabilityAccess: hasFeature(features, FEATURE_CODES.PROFITABILITY),
    hasEcmrAccess: hasFeature(features, FEATURE_CODES.ECMR),
    hasWhatsappNotificationsAccess: hasFeature(features, FEATURE_CODES.WHATSAPP_NOTIFICATIONS),
  };
  const effectiveUserData = userData ? { ...userData, ...featureAccess } : userData;

  useEffect(() => {
    if (!StorageService.get('userData')) {
      loadUserData();
    }
  }, []);

  const loadUserData = async () => {
    try {
      const token = StorageService.getAuthToken();
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await fetch(`${BASE_URL}profile`, {
        method: 'GET',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          // Token expired or invalid
          StorageService.clearAuthData();
          navigate('/login');
          return;
        }
        throw new Error('Failed to fetch profile');
      }

      const profile = await response.json();
      const synchronizedProfile = languageStore.applyUser(profile);
      StorageService.set('userData', synchronizedProfile);
      window.dispatchEvent(new CustomEvent('atrux:user-language', { detail: { user: synchronizedProfile } }));

      setUserData(toLayoutUserData(synchronizedProfile));
    } catch (error) {
      console.error('Error loading user data:', error);
      // Set default data if error occurs
      setUserData({
        name: 'Utilizator',
        fullName: 'Utilizator',
        company: 'AtruX Transport',
        role: 'user'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      // Import the enhanced auth service dynamically
      const { enhancedAuthService } = await import('../../services/auth/enhancedAuthService.js');
      await enhancedAuthService.logout();

      if (typeof onLogout === 'function') {
        onLogout();
      } else {
        // Clear auth data and redirect
        StorageService.clearAuthData();
        navigate('/login', { replace: true });
      }
    } catch (error) {
      console.error('Error during logout:', error);
      // Fallback: clear storage and redirect
      StorageService.clearAuthData();
      if (typeof onLogout === 'function') {
        onLogout();
      } else {
        navigate('/login', { replace: true });
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar Component */}
      <ModernSidebar
        userData={effectiveUserData}
      />

      {/* Main Content Area with TopBar */}
      <div className="dashboard-content dashboard-main relative">
        {/* Top Bar Component */}
        <TopBar
          onLogout={handleLogout}
          userData={effectiveUserData}
        />

        <main className="p-4 sm:p-6 lg:p-8 pt-4">
          <GlobalBackButton />
          {children}
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
